import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SharePositionCard } from "./share-position-card";
import { generateShareImage, shareViaWebShare, shareToTwitter } from "@/lib/share-utils";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { Position } from "@/solana/programs/types";

// X (Twitter) icon component
const XIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

type ShareResultModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: Position | null;
  pnl: number;
  exitPrice: number;
};

// Calculate scale based on viewport width for mobile responsiveness
const getCardScale = () => {
  if (typeof window === 'undefined') return 0.85;
  const viewportWidth = window.innerWidth;
  const cardWidth = 600;
  const padding = 48; // 24px on each side
  const availableWidth = viewportWidth - padding;
  
  if (availableWidth >= cardWidth) {
    return 0.85; // Desktop - slight scale down
  }
  // Mobile - scale to fit viewport with some margin
  return Math.min(0.85, (availableWidth / cardWidth) * 0.95);
};

export const ShareResultModal = ({
  open,
  onOpenChange,
  position,
  pnl,
  exitPrice,
}: ShareResultModalProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [cardScale, setCardScale] = useState(0.85);

  // Update scale on mount and resize
  useEffect(() => {
    const handleResize = () => setCardScale(getCardScale());
    handleResize(); // Set initial scale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate image when modal opens
  useEffect(() => {
    if (open && position) {
      // Small delay to ensure the card is rendered
      const timer = setTimeout(async () => {
        setIsGenerating(true);
        try {
          const blob = await generateShareImage("share-card");
          setImageBlob(blob);
        } catch (error) {
          console.error("Failed to generate share image:", error);
        }
        setIsGenerating(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setImageBlob(null);
    }
  }, [open, position]);

  const handleShare = useCallback(async () => {
    if (!imageBlob || !position) return;
    const isProfit = pnl >= 0;
    const text = isProfit
      ? `🚀 Just closed a ${position.market} ${position.side.toUpperCase()} trade on @NordexTrade with +$${Math.abs(pnl).toFixed(2)} profit! 📈\n\nTrade on nordex.trade`
      : `📊 Closed my ${position.market} ${position.side.toUpperCase()} position on @NordexTrade\n\nTrade on nordex.trade`;
    await shareViaWebShare(imageBlob, text);
  }, [imageBlob, position, pnl]);

  const handleTwitterShare = useCallback(async () => {
    if (!imageBlob || !position) return;
    const isProfit = pnl >= 0;
    const text = isProfit
      ? `🚀 Just closed a ${position.market} ${position.side.toUpperCase()} trade on @NordexTrade with +$${Math.abs(pnl).toFixed(2)} profit! 📈\n\nTrade on nordex.trade`
      : `📊 Closed my ${position.market} ${position.side.toUpperCase()} position on @NordexTrade\n\nTrade on nordex.trade`;
    
    const result = await shareToTwitter(imageBlob, text);
    
    if (result === "copied") {
      toast.success("Image copied to clipboard!", {
        description: "Paste it in your tweet (Ctrl+V / Cmd+V)",
      });
    } else if (result === "failed") {
      toast.error("Could not copy image", {
        description: "Please try the Share button instead",
      });
    }
    // If result === "shared", Web Share API handled it - no toast needed
  }, [imageBlob, position, pnl]);

  if (!position) return null;

  // Calculate margin compensation based on scale
  const marginCompensation = -600 * (1 - cardScale) * 0.75;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-w-[95vw] p-0 gap-0 bg-background/95 backdrop-blur-sm border-border/50 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            Share Your Result
          </DialogTitle>
        </DialogHeader>

        <div className="px-2 sm:px-6 pb-4 overflow-hidden">
          {/* Share Card Preview - scaled to fit viewport */}
          <div className="flex justify-center">
            <div 
              style={{ 
                transform: `scale(${cardScale})`,
                transformOrigin: "top center",
                marginBottom: `${marginCompensation}px`,
              }}
            >
              <SharePositionCard
                position={position}
                pnl={pnl}
                exitPrice={exitPrice}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons - Share on X and Share */}
        <div className="p-4 sm:p-6 pt-4 border-t border-border/50 bg-muted/30">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleTwitterShare}
              disabled={isGenerating || !imageBlob}
            >
              <XIcon className="size-4 mr-2" />
              Share on X
            </Button>
            <Button
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={handleShare}
              disabled={isGenerating || !imageBlob}
            >
              {isGenerating ? (
                <Loader2 className="size-4 mr-2 animate-spin" />
              ) : (
                <Share2 className="size-4 mr-2" />
              )}
              Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
